import type { LeaderboardCopy } from '../leaderboard-copy';
import type { LeaderboardEntry } from '../leaderboard.types';
import { LeaderboardPagination } from './LeaderboardPagination';
import { LeaderboardCard, LeaderboardTableRow } from './LeaderboardRow';

interface LeaderboardTableProps {
  copy: LeaderboardCopy;
  /** Rows to list, already filtered; the table pages through them. */
  entries: LeaderboardEntry[];
  isMine: (entry: LeaderboardEntry) => boolean;
  format: (value: number) => string;
  languageName: (code: string) => string;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/** A table from md up, a list of cards below; both share the pagination. */
export function LeaderboardTable({ copy, entries, isMine, format, languageName, page, pageSize, onPageChange, onPageSizeChange }: LeaderboardTableProps) {
  const offset = (page - 1) * pageSize;
  const rows = entries.slice(offset, offset + pageSize);

  return (
    <div className="overflow-hidden rounded-lg border border-border/60 bg-surface">
      <table className="hidden w-full table-fixed md:table">
        <caption className="sr-only">{copy.tableLabel}</caption>
        <thead className="border-b border-border/60 bg-surface-2">
          <tr className="text-left text-xs font-medium text-muted-foreground">
            <th scope="col" className="w-16 px-4 py-2.5 font-medium">{copy.rank}</th>
            <th scope="col" className="px-4 py-2.5 font-medium">{copy.contributor}</th>
            <th scope="col" className="w-56 px-4 py-2.5 font-medium">{copy.languagesCol}</th>
            <th scope="col" className="w-36 px-4 py-2.5 text-right font-medium">{copy.value}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {rows.map((entry) => (
            <LeaderboardTableRow key={entry.userId ?? entry.rank} copy={copy} entry={entry} mine={isMine(entry)} format={format} languageName={languageName} />
          ))}
        </tbody>
      </table>

      <ul aria-label={copy.tableLabel} className="divide-y divide-border/60 md:hidden">
        {rows.map((entry) => (
          <LeaderboardCard key={entry.userId ?? entry.rank} copy={copy} entry={entry} mine={isMine(entry)} format={format} languageName={languageName} />
        ))}
      </ul>

      {entries.length > pageSize ? (
        <LeaderboardPagination
          copy={copy}
          page={page}
          pageSize={pageSize}
          total={entries.length}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      ) : null}
    </div>
  );
}
